'use client'

// app/components/Modal/Modal.tsx
import React, { useEffect, useRef } from 'react'
import styles from './Modal.module.css'

interface ModalProps {
  isOpen: boolean
  onClose: () => void
  title?: string
  children: React.ReactNode
  width?: string
  height?: string
}

export default function Modal({
  isOpen,
  onClose,
  title,
  children,
  width = '500px',
  height = 'auto'
}: ModalProps) {
  const modalRef = useRef<HTMLDivElement>(null)

  // Close when clicking outside the modal
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
        onClose()
      }
    }

    // Close on escape key press
    function handleEscapeKey(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        onClose()
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      document.addEventListener('keydown', handleEscapeKey)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('keydown', handleEscapeKey)
    }
  }, [isOpen, onClose])

  if (!isOpen) return null

  return (
    <div className={styles.modalOverlay}>
      <div 
        ref={modalRef} 
        className={styles.modalContent}
        style={{ width, height }}
      >
        {title && (
          <div className={styles.modalHeader}>
            <h3>{title}</h3>
            <button 
              className={styles.closeButton} 
              onClick={onClose}
              aria-label="Close"
            >
              ×
            </button>
          </div>
        )}
        <div className={styles.modalBody}>
          {children}
        </div>
      </div>
    </div>
  )
}